import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import './PricingPage.css';
import './RateCard.css';

const STEPS = [
  {
    title: 'Introduce a business',
    body: 'Any business whose phone drives revenue — home services, clinics, salons, law firms, property managers.',
  },
  {
    title: 'We run the free audit',
    body: 'We show them exactly which calls they are missing and what it costs. That audit is the door-opener; you do not have to be technical.',
  },
  {
    title: 'We build and launch',
    body: 'Call flows, integrations, testing, and go-live are ours. Most agents are live in a few days.',
  },
  {
    title: 'You earn every month',
    body: 'Commission is recurring for the life of the account, paid monthly on what the client actually pays.',
  },
];

const TIERS = [
  {
    name: 'Referral Partner',
    rate: '15%',
    qualifier: 'No minimum',
    blurb: 'You make the introduction. We handle the pitch, the build, and the relationship.',
    bullets: [
      'Recurring commission for the life of the account',
      'We run the audit, demo, and close',
      'Use our live demo line as your opener',
      'No technical work, no support burden',
    ],
  },
  {
    name: 'Reseller Partner',
    rate: '25%',
    qualifier: '3+ active clients',
    featured: true,
    blurb: 'You sell and own the client relationship. We build and operate the agent behind you.',
    bullets: [
      'Everything in Referral Partner',
      'Co-branded proposals and audit reports',
      'We join your sales calls when you want backup',
      'Your clients get the standard published rate card',
    ],
  },
  {
    name: 'White-Label Partner',
    rate: '30%+',
    qualifier: '10+ active clients',
    blurb: 'Our agents under your brand. Custom terms once volume justifies it.',
    bullets: [
      'Everything in Reseller Partner',
      'Agents presented under your brand',
      'Custom commercial terms',
      'Direct line to the person who builds your agents',
    ],
  },
];

export const FAQS = [
  {
    q: 'How much can I realistically earn?',
    a: 'Commission is a percentage of what your client actually pays each month, for as long as they stay. As an illustration, ten clients on the $899/month AI Front Desk package at 25% is roughly $2,248 a month in recurring commission. That is an example of the math, not a promise of results — what you earn depends entirely on how many businesses you bring and which packages they buy.',
  },
  {
    q: 'Do I need to be technical?',
    a: 'No. You never touch a call flow or an integration. Your job is the introduction and, at reseller level, the relationship. Everything technical — building the agent, connecting the CRM, testing, and keeping it running — is ours.',
  },
  {
    q: 'What do my clients pay?',
    a: 'They pay our standard packages, which start at $500/month for 24/7 call coverage, with a one-time setup fee that is waived on a 12-month agreement. You are not asked to discount your own commission to win a deal.',
  },
  {
    q: 'When and how do I get paid?',
    a: 'Monthly, on collected revenue, for the life of the account. If the client stays five years, you get paid for five years. If they churn, the commission stops with them.',
  },
  {
    q: 'Who owns the client?',
    a: 'At referral level, we do — you make the introduction and collect. At reseller and white-label level, the relationship is yours, and we operate behind you.',
  },
  {
    q: 'How do I start?',
    a: 'Email hello@ubotika.com or call (424) 434-4541 — the AI answers that line, which is the fastest way to understand what you would be selling. There is no application form and no certification course; we start with a conversation and your first introduction.',
  },
];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'UBOTIKA Partner Program',
    serviceType: 'AI receptionist reseller and referral program',
    description:
      'Refer, resell, or white-label UBOTIKA AI receptionists and earn 15–30% recurring commission for the life of every account. No technical work required.',
    provider: {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#organization`,
      name: 'UBOTIKA',
      url: SITE_URL,
      telephone: '+1-310-912-3548',
      email: 'hello@ubotika.com',
    },
    areaServed: { '@type': 'City', name: 'Los Angeles' },
    offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', priceCurrency: 'USD' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Partners', item: `${SITE_URL}/partners` },
    ],
  },
];

function PartnersPage() {
  return (
    <>
      <SEO
        title="Partner Program | Resell AI Receptionists | UBOTIKA"
        description="Refer, resell, or white-label UBOTIKA AI receptionists. Earn 15–30% recurring commission for the life of every account. We build and run the agent — you make the introduction."
        path="/partners"
        schema={schema}
      />
      <main id="main-content" className="pricing-page ratecard-page">
        <section className="pricing-hero">
          <h1>Sell AI receptionists. Earn recurring revenue.</h1>
          <p className="pricing-sub">
            If you already have the trust of businesses whose phone drives their revenue —
            agencies, IT consultants, bookkeepers, marketers, industry vendors — you are one
            introduction away from recurring income. You bring the relationship. We build the
            agent, run it, and pay you every month it stays live.
          </p>
        </section>

        <section className="ratecard-section">
          <h2>How it works</h2>
          <p className="ratecard-lede">
            Four steps. You are only responsible for the first one.
          </p>
          <div className="partner-steps">
            {STEPS.map((s) => (
              <article key={s.title} className="partner-step">
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-section">
          <h2>Commission</h2>
          <p className="ratecard-lede">
            Recurring, on collected revenue, for the life of the account. Tiers move up
            automatically as your book grows — there is nothing to reapply for.
          </p>
          <div className="ratecard-grid">
            {TIERS.map((t) => (
              <article key={t.name} className={`ratecard-card${t.featured ? ' featured' : ''}`}>
                {t.featured && <span className="ratecard-flag">Most partners</span>}
                <h3>{t.name}</h3>
                <div className="partner-rate">
                  {t.rate}
                  <small>Recurring commission · {t.qualifier}</small>
                </div>
                <p className="ratecard-setup">{t.blurb}</p>
                <ul className="ratecard-list">
                  {t.bullets.map((b) => (
                    <li key={b}><FaCheck /><span>{b}</span></li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <p className="ratecard-fine">
            Illustration, not a promise: ten clients on the $899/month AI Front Desk package at
            25% is about $2,248 a month in recurring commission. What you actually earn depends
            on how many businesses you bring and what they buy.
          </p>
        </section>

        <section className="ratecard-section">
          <h2>What you get — and what we don&apos;t pretend to offer</h2>
          <p className="ratecard-lede">
            UBOTIKA is a small Los Angeles studio, not a channel machine. So this is deliberately
            honest about what exists today.
          </p>
          <div className="ratecard-grid">
            <article className="ratecard-vertical">
              <h3>What you get</h3>
              <ul className="ratecard-list">
                <li><FaCheck /><span>A live demo line you can call from a client meeting: (424) 434-4541</span></li>
                <li><FaCheck /><span>A free missed-call audit that opens the conversation for you</span></li>
                <li><FaCheck /><span>Co-branded proposals and audit reports</span></li>
                <li><FaCheck /><span>We join your sales calls when you want backup</span></li>
                <li><FaCheck /><span>We build, launch, and operate every agent</span></li>
                <li><FaCheck /><span>Direct access to the person who builds it — not a ticket queue</span></li>
              </ul>
            </article>
            <article className="ratecard-vertical">
              <h3>What we don&apos;t have</h3>
              <ul className="ratecard-list">
                <li><FaCheck /><span>No partner portal — reporting comes from us directly</span></li>
                <li><FaCheck /><span>No certification course or badge to earn first</span></li>
                <li><FaCheck /><span>No co-marketing budget</span></li>
                <li><FaCheck /><span>No partner directory listing</span></li>
              </ul>
              <p className="ratecard-fine" style={{ marginTop: 0 }}>
                If a program promising all of the above is what you need, we are not the right
                fit yet. If you want a straight recurring cut and a product that demos itself in
                thirty seconds, we are.
              </p>
            </article>
          </div>
        </section>

        <section className="ratecard-section">
          <h2>Partner questions, answered straight</h2>
          <div className="pricing-faq-list">
            {FAQS.map((f) => (
              <article className="pricing-faq-item" key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-pilot">
          <h2>Start with one introduction.</h2>
          <p>
            No application, no course, no minimum. Call the demo line the way your client would,
            and if it impresses you, send us one business. We&apos;ll run the audit and you&apos;ll
            see exactly how the conversation goes.
          </p>
          <div className="local-actions">
            <a href="tel:+14244344541" className="cta-button">Call the AI demo: (424) 434-4541</a>
            <a href="mailto:hello@ubotika.com?subject=Partner%20program" className="local-secondary-cta">
              hello@ubotika.com
            </a>
          </div>
          <p className="ratecard-fine">
            Curious what your clients would pay? See{' '}
            <Link to="/pricing">pricing</Link> — plans start at $500/month.
          </p>
        </section>
        <Footer />
      </main>
    </>
  );
}

export default PartnersPage;
